<template>
    <v-dialog max-widht="80%" persistent style="border-radius: 10px;" v-model="show">
        <template v-slot:activator="{ on, attrs }">
            <v-tooltip top>
                <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                    <v-btn
                        v-on="{ ...on, ...tipOn }"
                        v-bind="{ ...attrs, ...tipAttrs }"
                        fab
                        dark
                        color="primary"
                        :small="size === 's'"
                        :large="size === 'l'"
                        class="mb-2 mr-2"
                    >
                        <v-icon>mdi-file-question</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('csv.helpDialog.title') }}</span>
            </v-tooltip>
        </template>
        <help-card :items="items" :title="title" :intro="intro" @close="show = false">
            <template v-slot:toolbarExtension>
                <v-dialog v-model="showExportDialog" max-width="50%">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-on="on" v-bind="attrs" color="primary" dark small class="mb-2 mr-2" fab>
                            <v-icon>mdi-file-export</v-icon>
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title>
                            <span>{{ $t('csv.helpDialog.exportTitle') }}</span>
                        </v-card-title>
                        <v-card-text>
                            <v-select
                                :items="exportTypeOptions"
                                item-text="text"
                                item-value="value"
                                v-model="tipoExport"
                                :label="$t('csv.helpDialog.exportLabel')"
                            ></v-select>
                            <v-select
                                v-if="hasExportFormats"
                                :items="exportFormats"
                                item-text="text"
                                item-value="value"
                                v-model="exportFormat"
                                :label="$t('csv.helpDialog.exportFormatLabel')"
                                class="mt-2"
                            ></v-select>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                dark
                                small
                                class="mb-2 mr-2"
                                :disabled="exportWorking"
                                @click="showExportDialog = false"
                            >{{ $t('button.cancel') }}</v-btn>
                            <v-btn
                                color="primary"
                                dark
                                small
                                class="mb-2 mr-2"
                                :loading="exportWorking"
                                :disabled="exportWorking"
                                @click="exportCSV"
                            >{{ $t('button.export') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </template>
        </help-card>
    </v-dialog>
</template>
<script>
import HelpCard from '../Cards/HelpCard.vue';
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import {
    addPendingSchedulerDownload,
    SCHEDULER_DOWNLOAD_TYPES,
} from '@/utils/schedulerDownloadPending';

export default {
    name: "HelpDialog",
    components: {
        HelpCard,
    },
    props: {
        fields: {
            type: Array,
            required: true
        },
        model: {
            type: String,
            required: true
        },
        size: {
            type: String,
            default() {
                return 'm';
            }
        },
        data: {
            type: Array,
            default() {
                return [];
            }
        },
        /**
         * Se definido, exportação "Dados" (abaixo do limite local) busca todos os
         * registros sob demanda — mesmo padrão de ExportTableCSV / loadItemsForExport.
         */
        dataLoader: {
            type: Function,
            default: null,
        },
        /** Path relativo a API_URL (ex.: getallpessoas). Obrigatório para enqueue no scheduler. */
        listEndpoint: {
            type: String,
            default: null,
        },
        exportFilters: {
            type: Object,
            default() {
                return {};
            },
        },
        requestParams: {
            type: Object,
            default() {
                return {};
            },
        },
        postFilters: {
            type: Object,
            default() {
                return {};
            },
        },
        exportSortBy: {
            type: Array,
            default() {
                return [];
            },
        },
        exportSortDesc: {
            type: Array,
            default() {
                return [];
            },
        },
        exportTotal: {
            type: Number,
            default: 0,
        },
        selectedIds: {
            type: Array,
            default() {
                return [];
            },
        },
        idField: {
            type: String,
            default: null,
        },
        localRowLimit: {
            type: Number,
            default: 1000,
        },
        /** Intro opcional; se omitido, usa o texto padrão de csv.helpDialog.ms1. */
        introText: {
            type: String,
            default: null,
        },
        /**
         * Formatos alternativos de colunas na exportação (template/dados).
         * Ex.: [{ value: 'current', text: '...', excludeKeys: ['cod_rota'] }]
         * Se omitido, exporta todas as colunas de `fields`.
         */
        exportFormats: {
            type: Array,
            default: null,
        },
    },
    data() {
        const formats = Array.isArray(this.exportFormats) ? this.exportFormats : [];
        return {
            show: false,
            showExportDialog: false,
            tipoExport: 'template',
            exportFormat: formats.length > 0 ? formats[0].value : null,
            exportWorking: false,
        };
    },
    computed: {
        hasExportFormats() {
            return Array.isArray(this.exportFormats) && this.exportFormats.length > 0;
        },
        activeExportFormat() {
            if (!this.hasExportFormats) {
                return null;
            }
            return (
                this.exportFormats.find((f) => f && f.value === this.exportFormat) ||
                this.exportFormats[0] ||
                null
            );
        },
        translatedModel() {
            if (this.$te(this.model)) {
                return this.$t(this.model);
            }
            return this.model;
        },
        intro() {
            if (this.introText != null && String(this.introText).trim() !== '') {
                return this.introText;
            }
            return this.$t('csv.helpDialog.ms1', {length: this.fields.length});
        },
        title() {
            return this.$t('csv.helpDialog.ms2', {model: this.translatedModel});
        },
        items() {
            let example = "";
            let csvFields = this.fields.map((f) => {
                example += f.example + ";";
                return {
                    title: f.label,
                    description: f.description,
                };
            });
            return [...csvFields, {title: this.$t('csv.helpDialog.example'), description: example.substring(0, example.length - 1)}];
        },
        exportTypeOptions() {
            return [
                { value: 'template', text: this.$t('csv.helpDialog.template') },
                { value: 'data', text: this.$t('csv.helpDialog.data') },
            ];
        },
        /** Colunas fixas da ajuda CSV (key = cabeçalho do template de importação). */
        templateColumns() {
            let fields = (this.fields || []).filter((f) => f && f.key);
            const fmt = this.activeExportFormat;
            if (fmt) {
                if (Array.isArray(fmt.excludeKeys) && fmt.excludeKeys.length > 0) {
                    const exclude = new Set(fmt.excludeKeys.map((k) => String(k)));
                    fields = fields.filter((f) => !exclude.has(String(f.key)));
                }
                if (Array.isArray(fmt.includeKeys) && fmt.includeKeys.length > 0) {
                    const include = new Set(fmt.includeKeys.map((k) => String(k)));
                    fields = fields.filter((f) => include.has(String(f.key)));
                }
            }
            return fields
                .map((f) => ({
                    key: String(f.key).trim(),
                    title: String(f.key).trim(),
                }))
                .filter((c) => c.key);
        },
        resolvedSelectedIds() {
            return Array.isArray(this.selectedIds)
                ? this.selectedIds.filter((id) => id != null && id !== '')
                : [];
        },
        exportRowCountEstimate() {
            if (this.resolvedSelectedIds.length > 0) {
                return this.resolvedSelectedIds.length;
            }
            const total = Number(this.exportTotal);
            return Number.isFinite(total) && total > 0 ? total : 0;
        },
        canEnqueueSchedulerExport() {
            return !!(this.listEndpoint && String(this.listEndpoint).trim());
        },
        effectiveLocalRowLimit() {
            const limite = Number(this.localRowLimit);
            return Number.isInteger(limite) && limite > 0 ? limite : 1000;
        },
        downloadFilename() {
            return this.translatedModel || 'export';
        },
    },
    methods: {
        escapeCsvCell(val) {
            if (val == null || val === '') return '';
            const s = String(val);
            if (/[;"\r\n]/.test(s)) {
                return '"' + s.replace(/"/g, '""') + '"';
            }
            return s;
        },
        buildCsvRows(sourceRows) {
            const keys = this.templateColumns.map((c) => c.key);
            if (keys.length === 0) {
                return [];
            }
            if (this.tipoExport === 'template' || !sourceRows || sourceRows.length === 0) {
                const header = {};
                keys.forEach((key) => {
                    header[key] = '';
                });
                return [header];
            }
            return sourceRows.map((d) => {
                const row = {};
                keys.forEach((key) => {
                    row[key] = d[key];
                });
                return row;
            });
        },
        async resolveExportSourceRows() {
            if (this.tipoExport === 'template') {
                return [];
            }
            if (typeof this.dataLoader === 'function') {
                const csvDetail = this.$t('system.labels.exportToCsv');
                this.$emit('export-loading', {
                    loading: true,
                    message: csvDetail,
                    progress: 0,
                });
                try {
                    const rows = await this.dataLoader((pct) => {
                        this.$emit('export-loading', {
                            loading: true,
                            message: csvDetail,
                            progress: pct,
                        });
                    });
                    return Array.isArray(rows) ? rows : [];
                } finally {
                    this.$emit('export-loading', { loading: false });
                }
            }
            return Array.isArray(this.data) ? this.data : [];
        },
        downloadCsvBlob(csvdata) {
            if (!csvdata || csvdata.length === 0) {
                return;
            }
            const keys = this.templateColumns.map((c) => c.key);
            if (keys.length === 0) {
                return;
            }
            const sep = ';';
            const lines = [keys.map((k) => this.escapeCsvCell(k)).join(sep)];
            csvdata.forEach((row) => {
                lines.push(keys.map((k) => this.escapeCsvCell(row[k])).join(sep));
            });
            const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${this.downloadFilename}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            this.showExportDialog = false;
        },
        async enqueueCsvExport() {
            const columns = this.templateColumns;
            if (columns.length === 0 || !this.canEnqueueSchedulerExport) {
                return;
            }

            const selectedIds = this.resolvedSelectedIds.slice();
            const data = {
                filename: this.downloadFilename,
                listEndpoint: String(this.listEndpoint).trim(),
                columns,
                filters: this.exportFilters && typeof this.exportFilters === 'object'
                    ? this.exportFilters
                    : {},
                sortBy: Array.isArray(this.exportSortBy) ? this.exportSortBy.slice() : [],
                sortDesc: Array.isArray(this.exportSortDesc)
                    ? this.exportSortDesc.map((d) => d === true || d === 'true')
                    : [],
                selectedIds,
            };
            if (this.requestParams && typeof this.requestParams === 'object' && Object.keys(this.requestParams).length > 0) {
                data.requestParams = this.requestParams;
            }
            if (this.postFilters && typeof this.postFilters === 'object' && Object.keys(this.postFilters).length > 0) {
                data.postFilters = this.postFilters;
            }
            if (this.idField) {
                data.idField = this.idField;
            }

            const response = await apiController.request(
                apiController.endpoints.exportTableCsv,
                { data, silent: true }
            );
            const body = (response && response.data) || {};
            if (body.erro || !body.id_processo || body.status !== 'PENDING') {
                throw new Error(body.mensagem || 'export failed');
            }

            addPendingSchedulerDownload({
                id_processo: body.id_processo,
                type: SCHEDULER_DOWNLOAD_TYPES.TABLE_CSV_EXPORT,
                meta: { filename: this.downloadFilename },
            });
            this.showExportDialog = false;
            EventBus.$emit('scheduler-download-enqueued');
            EventBus.$emit('show-alert', {
                mensagem: this.$t('system.labels.csvExportEnqueued'),
                tipo: 'info',
                duracao: 6000,
            });
        },
        async exportCSV() {
            if (this.exportWorking) {
                return;
            }

            this.exportWorking = true;
            try {
                if (this.tipoExport === 'template') {
                    this.downloadCsvBlob(this.buildCsvRows([]));
                    return;
                }

                const shouldEnqueue =
                    this.canEnqueueSchedulerExport &&
                    this.exportRowCountEstimate > this.effectiveLocalRowLimit;

                if (shouldEnqueue) {
                    await this.enqueueCsvExport();
                    return;
                }

                const sourceRows = await this.resolveExportSourceRows();
                if (
                    sourceRows.length > this.effectiveLocalRowLimit &&
                    this.canEnqueueSchedulerExport
                ) {
                    await this.enqueueCsvExport();
                    return;
                }

                this.downloadCsvBlob(this.buildCsvRows(sourceRows));
            } catch (_err) {
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('system.labels.csvExportFailed'),
                    tipo: 'error',
                });
            } finally {
                this.exportWorking = false;
            }
        }
    },
}
</script>
<style>
    
</style>
